import { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { HStack, Text } from '../../components/primitives';
import { Button } from '../../components/primitives/Button';
import { useTranslation } from '../../contexts/TranslationContext';
import { colors } from '../../theme/colors';
import { buildFileFormData, type PickedFile } from '../../utils/fileFormData';
import { saveBlobAsFile } from '../../utils/saveBlobAsFile';
import { BusinessProfileStep } from './components/BusinessProfileStep';
import { GeneratingStep } from './components/GeneratingStep';
import {
  ProductCatalogStep,
  type ProductDraft,
  type ProductEdit,
} from './components/ProductCatalogStep';
import { ReviewPlanStep } from './components/ReviewPlanStep';
import { SuccessStep } from './components/SuccessStep';
import { VoiceOnboardingScaffold } from './components/VoiceOnboardingScaffold';
import {
  useApplyPlan,
  useCreateOrganizationProduct,
  useDeleteOrganizationProduct,
  useDownloadProductsTemplate,
  useGeneratePlan,
  useImportOrganizationProducts,
  useOnboardingProfile,
  useOnboardingState,
  useOrganizationProducts,
  usePatchState,
  useSaveProfile,
  useSetOnboardingMode,
  useUpdateOrganizationProduct,
} from './hooks';
import { useProductMutationFeedback } from './hooks/useProductMutationFeedback';
import type {
  GeneratedPlan,
  OnboardingMode,
  OnboardingState,
  OrganizationProfile,
  SaveProfileInput,
} from './types';

type BusinessProfileFields = Omit<SaveProfileInput, 'organizationId' | 'website' | 'languages'>;

const PERSISTENT_STEPS = ['profile', 'products', 'review'] as const;

type PersistentStep = (typeof PERSISTENT_STEPS)[number];
type WizardStep = PersistentStep | 'generating' | 'success';

function stepIndexOf(step: WizardStep): number {
  return PERSISTENT_STEPS.indexOf(step as PersistentStep);
}

function isPlanLanguageReusable(
  plan: GeneratedPlan | null | undefined,
  language: GeneratedPlan['outputLanguage'],
): boolean {
  return !plan?.outputLanguage || plan.outputLanguage === language;
}

function resolveResumeStep(
  state: OnboardingState,
  language: GeneratedPlan['outputLanguage'],
): PersistentStep {
  if (state.generatedPlan && isPlanLanguageReusable(state.generatedPlan, language)) return 'review';
  if (state.currentStep >= stepIndexOf('products')) return 'products';
  return 'profile';
}

function toProfileFields(profile: OrganizationProfile): BusinessProfileFields {
  return {
    industry: profile.industry ?? '',
    productSummary: profile.productSummary ?? '',
    targetAudience: profile.targetAudience ?? '',
    salesMotion: profile.salesMotion ?? '',
    tone: profile.tone ?? '',
  };
}

const PRODUCTS_TEMPLATE_FILENAME = 'products-template.xlsx';
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface OnboardingWizardProps {
  organizationId: string;
  onComplete?: () => void;
}

export function OnboardingWizard({ organizationId, onComplete }: Readonly<OnboardingWizardProps>) {
  const { t, language } = useTranslation();
  const { data: onboardingState } = useOnboardingState(organizationId);
  const { data: serverProfile } = useOnboardingProfile(organizationId);
  const saveProfileMutation = useSaveProfile();
  const generatePlanMutation = useGeneratePlan();
  const applyPlanMutation = useApplyPlan();
  const setOnboardingModeMutation = useSetOnboardingMode();
  const patchStateMutation = usePatchState();
  const { data: products } = useOrganizationProducts(organizationId);
  const createProductMutation = useCreateOrganizationProduct();
  const deleteProductMutation = useDeleteOrganizationProduct();
  const updateProductMutation = useUpdateOrganizationProduct();
  const importProductsMutation = useImportOrganizationProducts();
  const downloadTemplateMutation = useDownloadProductsTemplate();
  const productFeedback = useProductMutationFeedback();

  const [step, setStep] = useState<WizardStep>('profile');
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  const [profileValues, setProfileValues] = useState<BusinessProfileFields | null>(null);
  const [hasResumed, setHasResumed] = useState(false);
  const [mode, setMode] = useState<OnboardingMode>('guided');
  const [hasHydratedMode, setHasHydratedMode] = useState(false);
  const furthestReachedIndexRef = useRef(0);

  // Resume at the furthest step the server recorded. A persisted plan wins
  // over `currentStep` so states written before the step was tracked still
  // land on review instead of restarting at the profile.
  useEffect(() => {
    if (hasResumed || !onboardingState) return;
    const resumeStep = resolveResumeStep(onboardingState, language);
    const reusableGeneratedPlan = isPlanLanguageReusable(onboardingState.generatedPlan, language)
      ? onboardingState.generatedPlan
      : null;
    setPlan(reusableGeneratedPlan ?? null);
    furthestReachedIndexRef.current = Math.max(
      stepIndexOf(resumeStep),
      onboardingState.currentStep,
    );
    setStep(resumeStep);
    setHasResumed(true);
  }, [hasResumed, language, onboardingState]);

  // Hydrate the persisted modality choice once per load so the selection
  // survives a reload (design.md B3). Only runs once — after hydration the
  // toggle below is the sole source of truth for `mode`.
  useEffect(() => {
    if (!hasHydratedMode && onboardingState?.onboardingMode) {
      setMode(onboardingState.onboardingMode);
      setHasHydratedMode(true);
    }
  }, [hasHydratedMode, onboardingState]);

  // Only forward progress is persisted: reload lands on the furthest step the
  // user reached, and stepping back is a local, reversible action.
  const goToStep = (nextStep: PersistentStep) => {
    setStep(nextStep);
    const nextIndex = stepIndexOf(nextStep);
    if (nextIndex <= furthestReachedIndexRef.current) return;
    furthestReachedIndexRef.current = nextIndex;
    patchStateMutation.mutate({ organizationId, action: 'advance-step', step: nextIndex });
  };

  const handleBack = () => {
    if (step === 'products') setStep('profile');
    if (step === 'review') setStep('products');
  };

  const handleSelectMode = (nextMode: OnboardingMode) => {
    setMode(nextMode);
    setOnboardingModeMutation.mutate({ organizationId, onboardingMode: nextMode });
  };

  const runGenerate = () => {
    setStep('generating');
    generatePlanMutation.mutate(
      { organizationId, outputLanguage: language },
      {
        onSuccess: (data) => {
          if (data.generatedPlan) {
            setPlan(data.generatedPlan);
            goToStep('review');
          }
        },
      },
    );
  };

  // The catalog step sits between profile and generation so inference — and
  // every agent prompt afterwards — can see the real offerings, not just the
  // free-text productSummary paragraph.
  const handleProfileNext = (profile: BusinessProfileFields) => {
    setProfileValues(profile);
    saveProfileMutation.mutate(
      { organizationId, ...profile },
      { onSuccess: () => goToStep('products') },
    );
  };

  // Coming forward again reuses the plan already in hand only when it matches
  // the active UI language. Plans are stored in English and rendered into the
  // org language at the read edge, so a freshly served plan always matches;
  // this still guards a plan cached before an in-session language switch.
  const handleProductsNext = () => {
    if (plan && isPlanLanguageReusable(plan, language)) {
      goToStep('review');
      return;
    }
    if (plan) setPlan(null);
    runGenerate();
  };

  const handleAddProduct = (draft: ProductDraft) => {
    createProductMutation.mutate({ organizationId, ...draft }, productFeedback.callbacks);
  };

  const handleRemoveProduct = (productId: string) => {
    deleteProductMutation.mutate({ organizationId, productId }, productFeedback.callbacks);
  };

  const handleUpdateProduct = (productId: string, edit: ProductEdit) => {
    updateProductMutation.mutate({ organizationId, productId, ...edit }, productFeedback.callbacks);
  };

  const handleImportFile = (file: PickedFile) => {
    importProductsMutation.mutate({ organizationId, file: buildFileFormData(file) });
  };

  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate(language, {
      onSuccess: (blob) => {
        void saveBlobAsFile(blob, PRODUCTS_TEMPLATE_FILENAME, XLSX_MIME_TYPE);
      },
    });
  };

  const handleRetryGenerate = () => {
    runGenerate();
  };

  // Takes the plan from ReviewPlanStep rather than reading `plan` state: the
  // review step folds in editor text that was typed but never Saved, and that
  // merge only exists in the plan it passes up.
  const handleApply = (planToApply: GeneratedPlan) => {
    applyPlanMutation.mutate(
      { organizationId, plan: planToApply, outputLanguage: language },
      { onSuccess: () => setStep('success') },
    );
  };

  const stepIndex = stepIndexOf(step);
  const isReviewVisible = step === 'review';
  // Stays mounted while the user steps back to products so the editors keep
  // their unsaved drafts; regeneration unmounts it and starts clean.
  const isReviewMounted = plan !== null && (isReviewVisible || step === 'products');
  const profileInitialValues =
    profileValues ?? (serverProfile ? toProfileFields(serverProfile) : undefined);

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.content}
      testID="onboarding-wizard"
    >
      {stepIndex >= 0 && (
        <HStack
          spacing="sm"
          align="center"
          justify="between"
          style={styles.navigation}
          testID="onboarding-wizard-navigation"
        >
          <HStack spacing="sm" align="center">
            {stepIndex > 0 && (
              <Button
                testID="onboarding-wizard-back"
                variant="outline"
                size="sm"
                onPress={handleBack}
                accessibilityLabel={t('onboardingWizard.navigation.back')}
              >
                {t('onboardingWizard.navigation.back')}
              </Button>
            )}
            <Text
              variant="caption"
              color={colors.text.secondary}
              testID="onboarding-wizard-step-counter"
            >
              {`${t('onboardingWizard.navigation.stepCounter')} ${stepIndex + 1} ${t('onboardingWizard.navigation.stepCounterOf')} ${PERSISTENT_STEPS.length}`}
            </Text>
          </HStack>
          {isReviewVisible && (
            <Button
              testID="onboarding-wizard-regenerate"
              variant="ghost"
              size="sm"
              onPress={runGenerate}
              accessibilityLabel={t('onboardingWizard.navigation.regenerate')}
            >
              {t('onboardingWizard.navigation.regenerate')}
            </Button>
          )}
        </HStack>
      )}

      {step === 'profile' && (
        <>
          <HStack spacing="sm" testID="onboarding-modality-toggle" style={styles.modalityToggle}>
            <Button
              testID="onboarding-modality-guided"
              variant={mode === 'guided' ? 'primary' : 'outline'}
              size="sm"
              onPress={() => handleSelectMode('guided')}
              accessibilityLabel={t('onboardingWizard.modality.guidedLabel')}
            >
              {t('onboardingWizard.modality.guidedLabel')}
            </Button>
            <Button
              testID="onboarding-modality-voice"
              variant={mode === 'voice' ? 'primary' : 'outline'}
              size="sm"
              onPress={() => handleSelectMode('voice')}
              accessibilityLabel={t('onboardingWizard.modality.voiceLabel')}
            >
              {t('onboardingWizard.modality.voiceLabel')}
            </Button>
          </HStack>

          {mode === 'guided' && (
            <BusinessProfileStep
              onNext={handleProfileNext}
              isSaving={saveProfileMutation.isPending}
              initialValues={profileInitialValues}
            />
          )}

          {mode === 'voice' && (
            <VoiceOnboardingScaffold onContinueGuided={() => handleSelectMode('guided')} />
          )}
        </>
      )}

      {step === 'products' && (
        <ProductCatalogStep
          organizationId={organizationId}
          errorMessageKey={productFeedback.errorKey}
          products={products ?? []}
          onAdd={handleAddProduct}
          onRemove={handleRemoveProduct}
          onUpdate={handleUpdateProduct}
          onNext={handleProductsNext}
          isAdding={createProductMutation.isPending}
          isUpdating={updateProductMutation.isPending}
          onImportFile={handleImportFile}
          isImporting={importProductsMutation.isPending}
          importResult={importProductsMutation.data ?? null}
          importError={importProductsMutation.isError}
          onDownloadTemplate={handleDownloadTemplate}
          isDownloadingTemplate={downloadTemplateMutation.isPending}
        />
      )}

      {step === 'generating' && (
        <GeneratingStep isError={generatePlanMutation.isError} onRetry={handleRetryGenerate} />
      )}

      {isReviewMounted && plan && (
        <View
          testID="onboarding-review-container"
          style={isReviewVisible ? styles.reviewVisible : styles.reviewHidden}
          accessibilityElementsHidden={!isReviewVisible}
          importantForAccessibility={isReviewVisible ? 'auto' : 'no-hide-descendants'}
        >
          <ReviewPlanStep
            organizationId={organizationId}
            plan={plan}
            onChange={setPlan}
            onApply={handleApply}
            isApplying={applyPlanMutation.isPending}
          />
        </View>
      )}

      {step === 'success' && <SuccessStep onGoToAgents={() => onComplete?.()} />}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    padding: 24,
  },
  navigation: {
    marginBottom: 16,
  },
  modalityToggle: {
    marginBottom: 16,
  },
  reviewVisible: {
    display: 'flex',
  },
  reviewHidden: {
    display: 'none',
  },
});
